import { lazy, Suspense, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import VideoOrderCard from "./VideoOrderCard";

// Lazy, not a static import: Header renders this modal on every page, so a
// static import dragged the whole posting wizard (and its bundled job-taxonomy
// catalogs, ~52 KB) into main.js — defeating the lazy /employer/post route in
// App.js and making every visitor download the employer flow. Both entry
// points now share the same on-demand chunk.
const PostJob = lazy(() => import("../page/employer/PostJob"));

// Desktop-only popup wrapper around the employer job-posting wizard —
// mobile keeps navigating to the full /employer/post page (see usePostAction
// / Header.jsx), this is purely a desktop convenience so posting a job (or
// posting from within "Миний зар") doesn't leave the current page.
//
// Portaled straight to document.body, same as the jobseeker composer
// (CandidateSearchDemo.jsx's showComposer). Rendered inline it sat inside
// Header, inside Layout's scroll-linked header wrapper (isScrolled flips
// that wrapper between relative and fixed) — scrolling the page behind this
// modal reflowed that shared ancestor and dragged the fixed sidebar's paint
// along with it. Outside the Layout tree entirely, that can't happen.
export default function PostJobModal({ isOpen, onClose }) {
  // The company and phone the wizard has resolved, to prefill the video-order card.
  const [contact, setContact] = useState({ companyName: "", phone: "" });
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="post-job-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        >
          {/* Dimming scrim. This was a white wash, which lightened the page rather than
              pushing it back and left the white wizard sitting on a barely-different
              white — the dialog had no edge. Lighter than the app's other modals
              (bg-black/50) on purpose: the page stays legible behind it. */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[3px]" onClick={onClose} />

          {/* Wizard + the demo video beside it. The row sits above the scrim (relative),
              so clicks on the gap between them still reach the scrim and close. */}
          <div className="relative flex items-center gap-5 w-full max-w-[680px] xl:max-w-[1000px] pointer-events-none">
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98, transition: { duration: 0.18 } }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              /* Fixed box, not content-sized: the wizard's steps differ in height
                 and a max-h-only shell resized (and re-centred) on every step. */
              className="relative flex-1 min-w-0 max-w-[680px] h-[min(720px,86vh)] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto"
            >
              <button
                onClick={onClose}
                aria-label="Хаах"
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <FiX size={20} className="text-slate-700" />
              </button>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <Suspense fallback={<PostJobModalSkeleton />}>
                  <PostJob onClose={onClose} onContactChange={setContact} />
                </Suspense>
              </div>
            </motion.div>

            {/* How posting works, on the right, with the offer to have one made under it.
                Not tied to the wizard's height: the video keeps its full size and the card just
                sits under it, the column centred beside the wizard — 60vh + ~264px still fits
                the screen, so nothing scrolls. Only from xl up — below that it would squeeze
                the form. The video is muted so it can autoplay; its controls turn sound on. */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 20, opacity: 0, transition: { duration: 0.18 } }}
              transition={{ type: "spring", stiffness: 320, damping: 30, delay: 0.05 }}
              className="hidden xl:flex flex-col gap-3 flex-none w-[300px] pointer-events-auto"
            >
              <video
                src="/img/post-job-demo.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="flex-none h-[min(520px,60vh)] aspect-[9/16] mx-auto rounded-3xl bg-black object-cover shadow-2xl"
              />
              <VideoOrderCard companyName={contact.companyName} phone={contact.phone} />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Placeholder while the wizard chunk downloads — mirrors its header + first
// fields so the modal doesn't flash empty on a slow connection.
function PostJobModalSkeleton() {
  return (
    <div className="p-8 animate-pulse" aria-hidden="true">
      <div className="h-7 w-48 bg-slate-200 rounded-lg" />
      <div className="mt-3 h-4 w-64 bg-slate-100 rounded" />
      <div className="mt-8 space-y-4">
        <div className="h-12 bg-slate-100 rounded-xl" />
        <div className="h-12 bg-slate-100 rounded-xl" />
        <div className="h-12 bg-slate-100 rounded-xl" />
      </div>
      <div className="mt-8 h-12 w-full bg-slate-200 rounded-xl" />
    </div>
  );
}
